import { BookingType } from "../typings/booking-inputs";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Counts for the location-card filter-match tooltip and aria label. */
type LocationFilterMatchCounts = {
  total: number;
  /** Present when the API sent matrix buckets; omit to use the legacy combined tooltip. */
  full?: number;
  partial?: number;
  matched?: number;
  bookingType?: BookingType | null;
};

/** Plural inventory noun keys by booking type for location filter-match copy. */
const BOOKING_TYPE_INVENTORY_NOUN_KEY: Record<string, string> = {
  [BookingType.WORKPLACE]: "_booking_inventoryNoun_workplaces",
  [BookingType.TEAM]: "_booking_inventoryNoun_workplaces",
  [BookingType.CONFERENCEZONE]: "_booking_inventoryNoun_rooms",
  [BookingType.PLACEZONE]: "_booking_inventoryNoun_rooms",
  [BookingType.OPENSPACE]: "_booking_inventoryNoun_rooms",
  [BookingType.PARKINGPLACE]: "_booking_inventoryNoun_parkingSpaces",
  [BookingType.PARKINGAREA]: "_booking_inventoryNoun_parkingSpaces",
  [BookingType.ELECTRICCHARGINGSTATIONPLACE]: "_booking_inventoryNoun_chargingStations",
  [BookingType.VEHICLE]: "_booking_inventoryNoun_vehicles"
};

export function bookingTypeInventoryNoun(
  bookingType: BookingType | null | undefined,
  t: Translate
): string {
  const key =
    (bookingType && BOOKING_TYPE_INVENTORY_NOUN_KEY[bookingType]) ??
    "_booking_inventoryNoun_places";
  return t(key);
}

function hasMatrixSplit(counts: LocationFilterMatchCounts): boolean {
  return counts.full != null || counts.partial != null;
}

export function formatLocationFilterMatchTooltip(
  counts: LocationFilterMatchCounts,
  t: Translate
): string {
  if (!hasMatrixSplit(counts)) {
    return t("_booking_earlyInventoryFilter_matchedCountTooltip", {
      total: counts.total,
      matched: counts.matched ?? 0
    });
  }

  const full = counts.full ?? 0;
  const partial = counts.partial ?? 0;
  const resource = bookingTypeInventoryNoun(counts.bookingType, t);
  const allClause = t("_booking_earlyInventoryFilter_matchAll", { count: full });
  const someClause = t("_booking_earlyInventoryFilter_matchSome", { count: partial });

  if (full > 0 && partial > 0) {
    return t("_booking_earlyInventoryFilter_matchedCountTooltip_both", {
      total: counts.total,
      all: allClause,
      some: someClause,
      resource
    });
  }
  if (full > 0) {
    return t("_booking_earlyInventoryFilter_matchedCountTooltip_all", {
      total: counts.total,
      all: allClause,
      resource
    });
  }
  if (partial > 0) {
    return t("_booking_earlyInventoryFilter_matchedCountTooltip_some", {
      total: counts.total,
      some: someClause,
      resource
    });
  }

  return t("_booking_earlyInventoryFilter_matchedCountTooltip_none", {
    total: counts.total,
    resource
  });
}

export function formatLocationFilterMatchAria(
  counts: LocationFilterMatchCounts,
  t: Translate
): string {
  if (!hasMatrixSplit(counts)) {
    return t("_booking_earlyInventoryFilter_matchedOfAvailableAria", {
      total: counts.total,
      matched: counts.matched ?? 0
    });
  }

  return formatLocationFilterMatchTooltip(counts, t);
}
